import { useEffect, useState } from "react";
import { styles } from "../../styles";
import { Field, ErrorNote } from "../common";
import { minutesToLabel } from "../../lib/time";
import type { Breed } from "../../lib/types";
import {
  loadRemembered, saveRemembered, clearRemembered, hasRemembered,
} from "../../lib/remember";

export interface BookingDetails {
  dogName: string;
  breed: string;
  ownerName: string;
  phone: string;
  email: string;
}

export function BookingForm({
  start, breeds, initialBreed, onSubmit, submitting, submitError,
}: {
  start: number;
  breeds: Breed[];
  initialBreed: string;
  onSubmit: (details: BookingDetails) => void;
  submitting: boolean;
  submitError: string | null;
}) {
  const remembered = loadRemembered();
  const [dogName, setDogName] = useState(remembered?.dogName ?? "");
  const [breed, setBreed] = useState(remembered?.breed || initialBreed);
  const [ownerName, setOwnerName] = useState(remembered?.ownerName ?? "");
  const [phone, setPhone] = useState(remembered?.phone ?? "");
  const [email, setEmail] = useState(remembered?.email ?? "");
  const [remember, setRemember] = useState(hasRemembered());
  const [errs, setErrs] = useState<Partial<Record<keyof BookingDetails, string>>>({});

  // Edit-through: while the box is checked, keep stored values current. When it
  // is unchecked, clear the device immediately.
  useEffect(() => {
    if (remember) {
      saveRemembered({ dogName, breed, ownerName, phone, email });
    }
  }, [remember, dogName, breed, ownerName, phone, email]);

  const onToggleRemember = (checked: boolean) => {
    setRemember(checked);
    if (!checked) clearRemembered();
  };

  const forget = () => {
    clearRemembered();
    setRemember(false);
    setDogName("");
    setBreed(initialBreed);
    setOwnerName("");
    setPhone("");
    setEmail("");
  };

  // The breed select drives the actual appointment length the server will use.
  const selectedBreed = breeds.find((b) => b.name === breed);
  const minutes = selectedBreed?.appointmentMinutes ?? 0;

  const validate = (): boolean => {
    const e: Partial<Record<keyof BookingDetails, string>> = {};
    if (!dogName.trim()) e.dogName = "Tell us your dog's name.";
    if (!breed.trim() || !selectedBreed) e.breed = "Choose your dog's breed.";
    if (!ownerName.trim()) e.ownerName = "We need your name.";
    if (!/[0-9].*[0-9].*[0-9]/.test(phone)) e.phone = "Enter a phone number we can reach you at.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) e.email = "Enter a valid email for the invite.";
    setErrs(e);
    return Object.keys(e).length === 0;
  };

  const submit = () => {
    if (!validate()) return;
    onSubmit({
      dogName: dogName.trim(),
      breed: breed.trim(),
      ownerName: ownerName.trim(),
      phone: phone.trim(),
      email: email.trim(),
    });
  };

  return (
    <div style={styles.formWrap} className="fade">
      <div style={styles.formHead}>
        <h3 style={styles.formTitle}>Who's coming in?</h3>
        {minutes > 0 && (
          <p style={styles.formMeta}>{minutesToLabel(start)} to {minutesToLabel(start + minutes)}</p>
        )}
      </div>

      {submitError && <ErrorNote>{submitError}</ErrorNote>}

      <Field label="Dog's name" value={dogName} onChange={setDogName} err={errs.dogName} placeholder="e.g. Biscuit" autoComplete="off" />

      <div style={styles.field}>
        <label htmlFor="bf-breed" style={styles.label}>Dog's breed</label>
        <select
          id="bf-breed" value={breed} onChange={(e) => setBreed(e.target.value)}
          className="input select" style={styles.breedSelect}
          aria-invalid={!!errs.breed} aria-describedby={errs.breed ? "bf-breed-e" : undefined}
        >
          <option value="" disabled>Choose a breed</option>
          {breeds.map((b) => <option key={b.id} value={b.name}>{b.name}</option>)}
        </select>
        {errs.breed && <span id="bf-breed-e" style={styles.errText} role="alert">{errs.breed}</span>}
      </div>

      <Field label="Your name" value={ownerName} onChange={setOwnerName} err={errs.ownerName} placeholder="e.g. Jordan Lee" autoComplete="name" />
      <Field label="Phone number" value={phone} onChange={setPhone} err={errs.phone} placeholder="(555) 123-4567" type="tel" autoComplete="tel" />
      <Field label="Email" value={email} onChange={setEmail} err={errs.email} placeholder="you@email.com" type="email" autoComplete="email" hint="Your calendar invite goes here." />

      <div style={styles.checkRow}>
        <input
          id="bf-remember" type="checkbox" checked={remember}
          onChange={(e) => onToggleRemember(e.target.checked)}
          style={styles.checkBox}
        />
        <label htmlFor="bf-remember" style={styles.checkLabel}>
          Remember my details on this device, so I don't retype them next time.
        </label>
        {hasRemembered() && (
          <button type="button" className="link" style={styles.forgetBtn} onClick={forget}>
            Forget my info
          </button>
        )}
      </div>

      <button className="primary" style={styles.primary} onClick={submit} disabled={submitting}>
        {submitting ? "Booking…" : "Confirm appointment"}
      </button>
    </div>
  );
}
